import React, { useEffect } from 'react'
import { Bell, Shield, Palette, Monitor, HardDrive, ChevronRight } from 'lucide-react'
import { useBreadcrumbs } from '@renderer/context/BreadcrumbContext'

interface SettingsSectionProps {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
}

const SettingsSection: React.FC<SettingsSectionProps> = ({ icon: Icon, title, description }) => {
  return (
    <div className="flex items-center gap-4 p-4 hover:bg-[#f0e8d8]/50 transition-all cursor-pointer rounded-xl">
      <div
        className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
        style={{ background: '#f5ede0' }}
      >
        <Icon className="w-5 h-5 text-[#7a6020]" />
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-400" />
    </div>
  )
}

export const SettingsPage = (): React.JSX.Element => {
  const settingsSections = [
    { icon: Monitor, title: 'Appearance', description: 'Customize the look and feel of the application' },
    { icon: Bell, title: 'Notifications', description: 'Configure notification preferences' },
    { icon: Shield, title: 'Privacy & Security', description: 'Manage your privacy and security settings' },
    { icon: Palette, title: 'Theme', description: 'Choose between light and dark mode' },
    { icon: HardDrive, title: 'Storage', description: 'Manage local storage and cache' }
  ]
  const { setBreadcrumbs } = useBreadcrumbs()
  
  useEffect(() => {
    setBreadcrumbs([{ label: 'Settings' }])
  }, [])

  return (
    <div>
      <div className="max-w-[1200px] mx-auto space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500">Manage your preferences and account settings</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#e0d4be] overflow-hidden">
          {settingsSections.map((section, i) => (
            <div key={section.title}>
              <SettingsSection
                icon={section.icon}
                title={section.title}
                description={section.description}
              />
              {i < settingsSections.length - 1 && (
                <div className="mx-4 border-t border-[#e0d4be]" />
              )}
            </div>
          ))}
        </div>

        <div className="pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-400 text-center">Aurilearn Labs v1.0.0</p>
        </div>
      </div>
    </div>
  )
}
